import { parseDocx } from "./docxParser.js";
import { parsePptx } from "./pptxParser.js";

export const IMPORT_LIMITS = {
  maxFileSizeMB: 25,
  // Radiology MCQ banks routinely carry ~3 images per question (stems and
  // options), so 60 rejected legitimate ~20-question decks. Extraction is
  // already capped at 12 images per request; this bound only guards the
  // form against pathological files.
  maxImages: 120,
  maxQuestions: 100
};

const SUPPORTED_EXTENSIONS = [".docx", ".pptx"];

export class DocumentParseError extends Error {}

// Cheap non-crypto hash (FNV-1a over sample bytes) — crypto.subtle is
// unavailable on plain-http dev hosts, and this only needs to dedupe
// identical media entries within one file.
function hashBlob(blob) {
  // sample head + tail + size to stay cheap on multi-hundred-KB images
  return blob
    .slice(0, 8192)
    .arrayBuffer()
    .then(async (head) => {
      let hash = 0x811c9dc5;
      const bytes = new Uint8Array(head);
      for (const byte of bytes) {
        hash ^= byte;
        hash = Math.imul(hash, 0x01000193);
      }
      const tail =
        blob.size > 8192
          ? new Uint8Array(await blob.slice(-8192).arrayBuffer())
          : [];
      for (const byte of tail) {
        hash ^= byte;
        hash = Math.imul(hash, 0x01000193);
      }
      return `${hash}_${blob.size}`;
    });
}

function assertSupported(file) {
  const name = file.name || "";
  const extension = name.slice(name.lastIndexOf(".")).toLowerCase();
  if (!SUPPORTED_EXTENSIONS.includes(extension)) {
    throw new DocumentParseError(
      `Unsupported file type "${
        extension || name
      }". Please upload a .docx or .pptx file.`
    );
  }
  if (file.size > IMPORT_LIMITS.maxFileSizeMB * 1024 * 1024) {
    throw new DocumentParseError(
      `File is larger than the ${IMPORT_LIMITS.maxFileSizeMB}MB limit.`
    );
  }
}

/**
 * Revoke the preview object URLs created for a parse result.
 *
 * Call this only when a result is discarded without being handed to the Add
 * form. Once the form owns the images their previews still need the URLs, so
 * revoking them there would break the UI.
 */
export function releaseImages(images) {
  for (const image of Object.values(images || {})) {
    if (image && image.url) URL.revokeObjectURL(image.url);
  }
}

/**
 * Parse an uploaded .docx/.pptx into ordered text units + deduplicated images.
 *
 * @returns {Promise<{
 *   fileName: string, kind: "docx"|"pptx",
 *   units: Array<{index: number, label: string, text: string, imageIds: string[]}>,
 *   images: Object<string, {id: string, file: File, url: string}>
 * }>}
 */
export async function parseDocument(file) {
  assertSupported(file);

  const images = {};
  const seenHashes = new Map();
  let imageCounter = 0;

  const createImage = async (name, blob) => {
    const hash = await hashBlob(blob);
    if (seenHashes.has(hash)) return seenHashes.get(hash);

    const extension = (blob.type.split("/")[1] || "png")
      .replace("jpeg", "jpg")
      .replace(/[^a-z0-9]/gi, "");
    const fileName = `${name}.${extension}`;
    const imageFile = new File([blob], fileName, {
      type: blob.type || "image/png"
    });
    const id = `image_${++imageCounter}`;
    images[id] = {
      id,
      file: imageFile,
      url: URL.createObjectURL(imageFile)
    };
    seenHashes.set(hash, id);
    return id;
  };

  const isPptx = file.name.toLowerCase().endsWith(".pptx");
  let kind;
  let units;
  try {
    ({ kind, units } = isPptx
      ? await parsePptx(file, createImage)
      : await parseDocx(file, createImage));
  } catch (error) {
    // Images already registered would otherwise leak their object URLs.
    releaseImages(images);
    throw error;
  }

  const imageCount = Object.keys(images).length;
  if (imageCount > IMPORT_LIMITS.maxImages) {
    releaseImages(images);
    throw new DocumentParseError(
      `The document contains ${imageCount} images; the limit is ${IMPORT_LIMITS.maxImages}. Please split the file.`
    );
  }

  return { fileName: file.name, kind, units, images };
}
